/**
 * Admin Dashboard
 * Dashboard for system administrators
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import authService from '../services/authService';
import interviewService from '../services/interviewService';
import {
  MdPeople,
  MdAssessment,
  MdEvent,
  MdManageAccounts,
  MdSettings,
  MdAdd,
  MdPersonAdd,
  MdNotifications,
  MdVideoLibrary,
  MdBadge
} from 'react-icons/md';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [interviewCount, setInterviewCount] = useState(0);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllStats();
  }, []);

  const fetchAllStats = async () => {
    try {
      // Get date range - from now to 3 months ahead for all scheduled interviews
      const now = new Date();
      const threeMonthsAhead = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
      
      const [appResponse, userResponse, calendarResponse] = await Promise.all([
        applicationService.getApplicationStats(),
        authService.getUserStats(),
        interviewService.getCalendar(now.toISOString(), threeMonthsAhead.toISOString())
      ]);

      if (appResponse.success) {
        setStats(appResponse.data);
      }

      if (userResponse.success) {
        setUserStats(userResponse.data);
      }

      if (calendarResponse.success) {
        // Flatten calendar data to get all events (backward compatible with old interviews key)
        const allInterviews = [];
        calendarResponse.data.forEach(day => {
          const dayEvents = day.events || day.interviews || [];
          if (dayEvents.length > 0) {
            allInterviews.push(...dayEvents);
          }
        });
        
        // Keep only scheduled interview events
        const scheduled = allInterviews.filter(
          interview => interview.eventType === 'interview' && interview.status === 'SCHEDULED'
        );
        
        setInterviewCount(scheduled.length);
        
        // Get upcoming interviews (sorted by date, limit to 5)
        const upcoming = scheduled
          .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt))
          .slice(0, 5);
        
        setUpcomingInterviews(upcoming);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatInterviewDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalApplications = stats?.total || 0;
  const activeApplications = totalApplications - (stats?.byStage?.COMPLETED || 0) - (stats?.byStage?.REJECTED || 0);
  const hrCount = userStats?.byRole?.HR || 0;

  const rightSidebarContent = (
    <div className="p-4">
      <h3 className="text-base font-semibold text-charcoal mb-4 pb-2 border-b border-border">Recent Activity</h3>
      <div className="flex flex-col gap-3">
        <div className="flex gap-3 p-3 bg-background-secondary rounded-lg transition-colors hover:bg-[#e8e4d8]">
          <MdPeople className="text-olive flex-shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-charcoal m-0 mb-1 leading-snug">New user registered</p>
            <span className="text-[11px] text-text-secondary">2 hours ago</span>
          </div>
        </div>
        <div className="flex gap-3 p-3 bg-background-secondary rounded-lg transition-colors hover:bg-[#e8e4d8]">
          <MdSettings className="text-olive flex-shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-charcoal m-0 mb-1 leading-snug">System configuration updated</p>
            <span className="text-[11px] text-text-secondary">5 hours ago</span>
          </div>
        </div>
        <div className="flex gap-3 p-3 bg-background-secondary rounded-lg transition-colors hover:bg-[#e8e4d8]">
          <MdAssessment className="text-olive flex-shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-charcoal m-0 mb-1 leading-snug">Monthly report generated</p>
            <span className="text-[11px] text-text-secondary">1 day ago</span>
          </div>
        </div>
        <div className="flex gap-3 p-3 bg-background-secondary rounded-lg transition-colors hover:bg-[#e8e4d8]">
          <MdNotifications className="text-olive flex-shrink-0" size={20} />
          <div className="flex-1 min-w-0">
            <p className="text-[13px] text-charcoal m-0 mb-1 leading-snug">Notification sent to all users</p>
            <span className="text-[11px] text-text-secondary">2 days ago</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <MainLayout rightSidebarContent={rightSidebarContent}>
      <div className="min-h-screen bg-background p-lg">
        <div className="flex justify-between items-center mb-xl p-lg px-xl bg-surface rounded-lg shadow-sm border border-border-light">
          <div>
            <h1 className="text-[28px] text-primary m-0 font-semibold tracking-[-0.5px]">Admin Dashboard</h1>
            <p className="text-sm text-text-secondary mt-1 font-normal">System administration and configuration</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-lg max-w-full">
          <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-lg col-span-full">
            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdPeople className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Total Users</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">{loading ? '...' : userStats?.total || 0}</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" onClick={() => navigate('/hr/applications')}>
              <MdAssessment className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Active Applications</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">{loading ? '...' : activeApplications}</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdEvent className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Interviews Scheduled</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">{loading ? '...' : interviewCount}</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
              <MdManageAccounts className="text-[32px] w-14 h-14 flex items-center justify-center bg-background-light rounded-md flex-shrink-0 text-accent-green" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">HR Staff</h3>
                <p className="text-2xl text-primary m-0 font-bold leading-none">{loading ? '...' : hrCount}</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" onClick={() => navigate('/admin/create-application')}>
              <MdAdd className="text-[32px] w-14 h-14 flex items-center justify-center bg-blue-100 rounded-md flex-shrink-0 text-blue-600" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Create Application</h3>
                <p className="text-sm text-primary m-0 font-medium leading-none">For Candidates</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" onClick={() => navigate('/admin/create-user')}>
              <MdPersonAdd className="text-[32px] w-14 h-14 flex items-center justify-center bg-green-100 rounded-md flex-shrink-0 text-green-600" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Create User</h3>
                <p className="text-sm text-primary m-0 font-medium leading-none">All Roles & Profiles</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" onClick={() => navigate('/admin/tutorial-videos')}>
              <MdVideoLibrary className="text-[32px] w-14 h-14 flex items-center justify-center bg-purple-100 rounded-md flex-shrink-0 text-purple-600" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">Tutorial Videos</h3>
                <p className="text-sm text-primary m-0 font-medium leading-none">Landing Page Videos</p>
              </div>
            </div>

            <div className="bg-surface p-lg rounded-lg shadow-sm border border-border-light flex flex-col items-center text-center gap-md transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 cursor-pointer" onClick={() => navigate('/admin/id-card')}>
              <MdBadge className="text-[32px] w-14 h-14 flex items-center justify-center bg-yellow-100 rounded-md flex-shrink-0 text-yellow-600" size={32} />
              <div className="flex-1 flex flex-col items-center">
                <h3 className="text-[13px] text-text-secondary m-0 mb-1 font-medium uppercase tracking-wider">ID Card</h3>
                <p className="text-sm text-primary m-0 font-medium leading-none">Generate HR ID Cards</p>
              </div>
            </div>
          </div>

          <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
            <h3 className="text-lg text-primary mb-lg pb-md border-b-2 border-border-light font-semibold">Your Profile</h3>
            <div className="flex justify-between items-center py-md border-b border-border-light">
              <span className="text-text-secondary font-medium text-sm">Phone Number:</span>
              <span className="text-text-primary font-semibold text-sm">{user?.phoneNumber}</span>
            </div>
            {user?.email && (
              <div className="flex justify-between items-center py-md border-b border-border-light">
                <span className="text-text-secondary font-medium text-sm">Email:</span>
                <span className="text-text-primary font-semibold text-sm">{user.email}</span>
              </div>
            )}
            <div className="flex justify-between items-center py-md">
              <span className="text-text-secondary font-medium text-sm">Role:</span>
              <span className="px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-[rgba(193,122,92,0.15)] text-accent-terracotta-dark">ADMIN</span>
            </div>
          </div>

          <div className="bg-surface p-xl rounded-lg shadow-sm border border-border-light transition-all duration-200 hover:shadow-md">
            <h3 className="text-lg text-primary mb-lg pb-md border-b-2 border-border-light font-semibold">Upcoming Interviews</h3>
            {loading ? (
              <p className="text-center text-text-secondary p-xl text-sm">Loading...</p>
            ) : upcomingInterviews.length > 0 ? (
              <div className="flex flex-col gap-md mb-lg">
                {upcomingInterviews.map((interview) => (
                  <div key={interview.interviewId} className="p-md bg-background-light rounded-md border-l-[3px] border-accent-green transition-all duration-200 hover:bg-background hover:translate-x-1">
                    <div className="flex items-center gap-2 text-accent-green text-[13px] font-semibold mb-1.5">
                      <MdEvent size={18} />
                      <span>{formatInterviewDate(interview.scheduledAt)}</span>
                    </div>
                    <div className="text-[15px] font-semibold text-primary mb-1">
                      {interview.candidateName}
                    </div>
                    <div className="text-[13px] text-text-secondary">{interview.jobPosting}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-text-secondary p-xl text-sm">No upcoming interviews</p>
            )}
            <button 
              className="w-full p-3 bg-accent-green text-white border-none rounded-md font-semibold text-sm cursor-pointer transition-all duration-200 hover:bg-[#5a6b4d] hover:-translate-y-px"
              onClick={() => navigate('/hr/interviews')}
            >
              View All Interviews
            </button>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminDashboard;
